import { useState } from 'react'
import { FilterSchema } from '../../../../services/api'
import { Product } from '../../../../types/product'
import { countActiveShopFilters } from '../../../../utils/shopQuery'
import Filters from '../../../section/catalog/Filters/Filters'
import SortingFilter from '../../../section/catalog/SortingFilter/SortingFilter'
import FilterSkeleton from '../../../ui/FilterSkeleton/FilterSkeleton'
import ProductItem from '../../../ui/ProductItem/ProductItem'
import ProductSkeleton from '../../../ui/ProductSkeleton/ProductSkeleton'
import s from './GridProducts.module.css'

type SortOption = 'popularity' | 'price-asc' | 'price-desc' | 'newest'

interface GridProductsProps {
  products: Product[]
  loading?: boolean
  schema?: FilterSchema
  onFiltersChange?: (filters: Record<string, string[] | [number, number]>) => void
  categoryOptions?: { value: string; label: string }[]
  initialCategory?: string | null
  onCategoryChange?: (category: string | null) => void
  initialFilters?: Record<string, string[] | [number, number]>
  allProductsForRanges?: Product[]
  onSortChange?: (sortOption: SortOption) => void
  initialSort?: SortOption
}

export default function GridProducts({
  products,
  loading = false,
  schema,
  onFiltersChange,
  categoryOptions,
  initialCategory,
  onCategoryChange,
  initialFilters,
  allProductsForRanges,
  onSortChange,
  initialSort = 'popularity',
}: GridProductsProps) {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false)
  const activeFilterCount = countActiveShopFilters(initialFilters || {}, schema)

  const handleFiltersToggle = () => {
    setIsFiltersOpen(!isFiltersOpen)
  }

  const handleFiltersClose = () => {
    setIsFiltersOpen(false)
  }

  const handleOverlayClick = () => {
    setIsFiltersOpen(false)
  }

  // Render skeleton loaders when loading
  if (loading) {
    return (
      <div className={s.wrapProducts}>
        <div className={s.filtersWrap}>
          <FilterSkeleton />
          <Filters
            schema={schema}
            isOpen={isFiltersOpen}
            onClose={handleFiltersClose}
            onFiltersChange={onFiltersChange}
            categoryOptions={categoryOptions}
            initialCategory={initialCategory}
            onCategoryChange={onCategoryChange}
            initialFilters={initialFilters}
          />
          <SortingFilter onSortChange={onSortChange} initialSort={initialSort} />
        </div>
        <div className={s.gridProducts}>
          {[...Array(9)].map((_, index) => (
            <ProductSkeleton key={index} />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={s.wrapProducts}>
      {isFiltersOpen && <div className={s.overlay} onClick={handleOverlayClick} />}
      <div className={s.filtersWrap}>
        <button className={s.filterOpen} onClick={handleFiltersToggle}>
          {`Фільтри${activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}`}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M1.33268 8L14.666 8"
              stroke="#0D0D0D"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M8 1.33335L8 14.6667"
              stroke="#0D0D0D"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <Filters
          schema={schema}
          isOpen={isFiltersOpen}
          onClose={handleFiltersClose}
          onFiltersChange={onFiltersChange}
          categoryOptions={categoryOptions}
          initialCategory={initialCategory}
          onCategoryChange={onCategoryChange}
          initialFilters={initialFilters}
          products={allProductsForRanges || products}
        />
        <SortingFilter onSortChange={onSortChange} initialSort={initialSort} />
      </div>
      <div className={s.gridProducts}>
        {products.length > 0 ? (
          products.map(product => <ProductItem key={product.id} product={product} />)
        ) : (
          <div className={s.emptyState}>
            <p className={s.emptyTitle}>Нічого не знайдено</p>
            <p className={s.emptyText}>Спробуй скинути фільтри або обрати іншу категорію.</p>
            {onFiltersChange && (
              <button
                type="button"
                className={s.emptyReset}
                onClick={() => {
                  onFiltersChange({})
                  onCategoryChange?.(null)
                }}
              >
                Скинути фільтри
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
